/**
 * <et-sfs-sticky-atc> — sticky add-to-cart bar for the Slice Fix System page.
 *
 * It is a satellite of the bundle product section rather than a second buy box:
 *  - the price, compare-at price and badges are mirrored from the buy box, so
 *    they can never disagree with it;
 *  - the option pills are cloned from the buy box and write back to it, keeping
 *    one source of truth for the selected variant;
 *  - the button calls the buy box's addToCart(), so the cart payload, cart
 *    refresh and drawer behaviour are identical.
 *
 * The bar reveals itself once the main add-to-cart button has scrolled out of
 * view, and stays hidden when there is no buy box on the page.
 */
if (!customElements.get('et-sfs-sticky-atc')) {
  class EtSfsStickyAtc extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.et-sfs-sticky');
      this.button = this.querySelector('[data-et-sticky-atc]');
      this.errorEl = this.querySelector('[data-et-sticky-error]');
      this.pricesEl = this.querySelector('[data-et-sticky-prices]');
      this.optionsEl = this.querySelector('[data-et-sticky-options]');

      this.product = document.querySelector('[data-et-buy-box]');
      if (!this.product) {
        this.remove();
        return;
      }

      customElements.whenDefined(this.product.localName).then(() => {
        this.cloneFigure();
        this.cloneOptions();
        this.sync();
        this.observeBuyBox();
      });

      this.onVariantChange = () => this.sync();
      document.addEventListener('et-sfs:variant-change', this.onVariantChange);

      if (this.button) {
        this.button.addEventListener('click', () => this.onClick());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('et-sfs:variant-change', this.onVariantChange);
      if (this.observer) this.observer.disconnect();
    }

    /* ---------------- Product figure ---------------- */

    /*
      Copies the buy box's first thumbnail — the gallery image together with the
      miniature free-product overlay — so the sticky bar shows the same artwork.
      Falls back to whatever the section setting rendered.
    */
    cloneFigure() {
      const figure = this.querySelector('[data-et-sticky-figure]');
      if (!figure) return;

      const source =
        this.product.querySelector('[data-et-thumb]') || this.product.querySelector('[data-et-slide]');
      if (!source) return;

      const image = source.querySelector('img');
      if (!image) return;

      figure.textContent = '';
      figure.appendChild(image.cloneNode(true));

      const overlay = source.querySelector('.et-sfs-pdp__thumb-overlay');
      if (overlay) {
        figure.appendChild(overlay.cloneNode(true));
      } else {
        // Slides carry the full-size overlay, so wrap it for the miniature scale
        const slideOverlay = source.querySelector('.et-sfs-pdp__overlay');
        if (slideOverlay) {
          const wrapper = document.createElement('span');
          wrapper.className = 'et-sfs-pdp__thumb-overlay';
          wrapper.setAttribute('aria-hidden', 'true');
          wrapper.appendChild(slideOverlay.cloneNode(true));
          figure.appendChild(wrapper);
        }
      }

      // The cards carry bundle item prices, which are converted per currency
      if (window.ETSfsMoney) window.ETSfsMoney.convert(figure);
    }

    /* ---------------- Variant picker ---------------- */

    cloneOptions() {
      if (!this.optionsEl) return;

      const groups = this.product.querySelectorAll('[data-et-option-group]');
      if (!groups.length) {
        this.optionsEl.remove();
        this.optionsEl = null;
        return;
      }

      groups.forEach((group, groupIndex) => {
        const clone = group.cloneNode(true);
        clone.removeAttribute('data-et-option-group');

        // Rewrite ids and names so the clone cannot collide with the buy box
        clone.querySelectorAll('input[type="radio"]').forEach((input, valueIndex) => {
          const id = `et-sfs-sticky-${groupIndex}-${valueIndex}`;
          const label = clone.querySelector(`[for="${input.id}"]`);
          input.id = id;
          input.name = `et-sfs-sticky-${groupIndex}`;
          input.removeAttribute('data-et-option-input');
          if (label) label.setAttribute('for', id);

          input.addEventListener('change', () => this.selectInBuyBox(groupIndex, input.value));
        });

        this.optionsEl.appendChild(clone);
      });
    }

    // Writing back to the buy box keeps a single selected variant on the page
    selectInBuyBox(groupIndex, value) {
      const groups = this.product.querySelectorAll('[data-et-option-group]');
      const group = groups[groupIndex];
      if (!group) return;

      const target = Array.from(group.querySelectorAll('[data-et-option-input]')).find(
        (input) => input.value === value
      );
      if (!target || target.checked) return;

      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------------- Mirroring ---------------- */

    sync() {
      this.syncPrices();
      this.syncOptions();
      this.syncButton();
    }

    syncPrices() {
      if (!this.pricesEl) return;
      const source = this.product.querySelector('.et-sfs-pdp__prices');
      if (source) this.pricesEl.innerHTML = source.innerHTML;
    }

    syncOptions() {
      if (!this.optionsEl) return;

      const groups = this.product.querySelectorAll('[data-et-option-group]');
      const clones = this.optionsEl.querySelectorAll('.et-sfs-pdp__option-group');

      clones.forEach((clone, groupIndex) => {
        const group = groups[groupIndex];
        if (!group) return;

        const checked = group.querySelector('[data-et-option-input]:checked');
        const selectedValue = checked ? checked.value : null;

        const label = clone.querySelector('[data-et-option-selected]');
        if (label) label.textContent = selectedValue || '';

        clone.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === selectedValue;

          const source = Array.from(group.querySelectorAll('[data-et-option-input]')).find(
            (original) => original.value === input.value
          );
          const cloneLabel = clone.querySelector(`[for="${input.id}"]`);
          if (source && cloneLabel) {
            cloneLabel.classList.toggle('is-unavailable', source.dataset.available === 'false');
          }
        });
      });
    }

    syncButton() {
      if (!this.button) return;

      const source = this.product.querySelector('[data-et-atc]');
      if (!source) return;

      this.button.disabled = source.disabled;

      const sourceText = source.querySelector('[data-et-atc-text]');
      const target = this.button.querySelector('[data-et-sticky-atc-text]');

      // Only mirror the sold out / unavailable states — the CTA copy is its own setting
      if (sourceText && target && source.disabled) {
        target.textContent = sourceText.textContent;
      } else if (target && target.dataset.label) {
        target.textContent = target.dataset.label;
      }
    }

    /* ---------------- Reveal ---------------- */

    observeBuyBox() {
      const anchor = this.product.querySelector('[data-et-atc]') || this.product;

      if (!('IntersectionObserver' in window)) {
        this.toggle(true);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Show once the buy box button has scrolled above the viewport
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.toggle(scrolledPast);
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(anchor);
    }

    toggle(visible) {
      if (!this.bar) return;
      this.bar.classList.toggle('is-visible', visible);
      this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    /* ---------------- Add to cart ---------------- */

    onClick() {
      if (typeof this.product.addToCart !== 'function') return;

      this.showError('');

      this.product.addToCart(this.button).then((result) => {
        if (result && result.ok) return;
        this.showError((result && result.message) || '');
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }

  customElements.define('et-sfs-sticky-atc', EtSfsStickyAtc);
}
